import React, { useEffect, useMemo, useState } from 'react'
import { Card, CardBody, CardHeader, Col, Container, Row, Spinner, Button } from 'reactstrap'
import TableContainer from '../../Components/Common/TableContainerReactTable.js';
import useObtenerOrdenesCompra from '../../Components/Hooks/useObtenerOrdenesCompra.js';
import useCreateOrdenCompra from '../../Components/Hooks/useCreateOrdenCompra.js';
import useUpdateOrdenCompra from '../../Components/Hooks/useUpdateOrdenCompra.js';
import useDeleteOrdenCompra from '../../Components/Hooks/useDeleteOrdenCompra.js';
import ModalCrearOrdenCompra from '../../Components/modals/ModalCrearOrdenCompra.js';
import ModalModificarOrdenCompra from '../../Components/modals/ModalModificarOrdenCompra.js';
import ModalLeerOrdenCompra from '../../Components/modals/ModalLeerOrdenCompra.js';

const OrdenesCompra = () => {

    const [selectedOrdenCompra, setSelectedOrdenCompra] = useState(null);
    const [refresh, setRefresh] = useState(0);
    const { ordenesCompra, loading, error } = useObtenerOrdenesCompra(refresh);
    const [modalCrearOpen, setModalCrearOpen] = useState(false);
    const [modalModificarOpen, setModalModificarOpen] = useState(false);
    const [modalLeerOpen, setModalLeerOpen] = useState(false);
    const { createOrdenCompra, loading: createLoading, error: createError } = useCreateOrdenCompra();
    const { updateOrdenCompra, loading: updateLoading, error: updateError } = useUpdateOrdenCompra();
    const { deleteOrdenCompra, loading: deleteLoading, error: deleteError } = useDeleteOrdenCompra();

    useEffect(() => {
        document.title = "Órdenes de Compra | Siga ERP";
    }, []);

    const columns = useMemo(
        () => [
            {
                header: "Empresa",
                accessorKey: "CLAEMP",
                enableColumnFilter: false,
            },
            {
                header: "Número Orden",
                accessorKey: "NROODC",
                enableColumnFilter: false,
            },
            {
                header: "Fecha Orden",
                accessorKey: "FECODC",
                enableColumnFilter: false,
                cell: ({ getValue }) => getValue ? new Date(getValue).toLocaleDateString() : '',
            },
            {
                header: "Estado",
                accessorKey: "ESTADO",
                enableColumnFilter: false,
                cell: ({ getValue }) => {
                    const estado = getValue;
                    switch (estado) {
                        case 0: return 'Vigente';
                        case 1: return 'Con recepción parcial';
                        case 2: return 'Recepción completa';
                        case 3: return 'Cerrada';
                        default: return '';
                    }
                },
            },
            {
                header: "Valor Total",
                accessorKey: "VALTOT",
                enableColumnFilter: false,
            },
        ],
        []
    );

    const handleCellClick = (cellInfo) => {
        setSelectedOrdenCompra(cellInfo.rowData);
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="d-flex justify-content-between align-items-center">
                                    <h5 className="card-title mb-0">Órdenes de Compra</h5>
                                    <div className="d-flex align-items-center">
                                        <Button color="primary" className="me-2" onClick={() => setModalCrearOpen(true)} disabled={createLoading}>
                                            <i className="ri-add-line me-1"></i>Crear
                                        </Button>
                                        <Button color="info" className="me-2" onClick={() => { if (selectedOrdenCompra) setModalLeerOpen(true); }} disabled={!selectedOrdenCompra}>
                                            <i className="ri-eye-line me-1"></i>Ver
                                        </Button>
                                        <Button color="warning" className="me-2" onClick={() => { if (selectedOrdenCompra) setModalModificarOpen(true) }} disabled={!selectedOrdenCompra || updateLoading}>
                                            <i className="ri-edit-line me-1"></i>Modificar
                                        </Button>
                                        <Button color="danger" className="me-2" onClick={async () => {
                                            if (selectedOrdenCompra && window.confirm('¿Está seguro que desea eliminar esta orden de compra? Esta acción no se puede deshacer.')) {
                                                const result = await deleteOrdenCompra(selectedOrdenCompra.CLAEMP, selectedOrdenCompra.NROODC);
                                                if (result) {
                                                    alert('Orden de compra eliminada');
                                                    setRefresh(prev => prev + 1);
                                                } else {
                                                    alert('Error: No se pudo eliminar');
                                                }
                                            }
                                        }} disabled={!selectedOrdenCompra || deleteLoading}>
                                            <i className="ri-delete-bin-line me-1"></i>Eliminar
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardBody>
                                    {error ? <div className="alert alert-danger">{error}</div> : loading ? <div className="text-center"><Spinner animation="border" variant="primary" /></div> : <TableContainer
                                        columns={(columns || [])}
                                        data={ordenesCompra?.records || []}
                                        customPageSize={15}
                                        isGlobalFilter={true}
                                        SearchPlaceholder='Buscar...'
                                        onCellClick={handleCellClick}
                                    />}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
            <ModalCrearOrdenCompra
                isOpen={modalCrearOpen}
                toggle={() => setModalCrearOpen(false)}
                onSave={async (formData) => {
                    const result = await createOrdenCompra(formData);
                    if (result) {
                        alert('Orden de compra creada exitosamente');
                        setModalCrearOpen(false);
                        setRefresh(prev => prev + 1);
                    } else {
                        alert('Error al crear orden de compra: ' + createError);
                    }
                }}
            />
            <ModalModificarOrdenCompra
                isOpen={modalModificarOpen}
                toggle={() => setModalModificarOpen(false)}
                selectedOrdenCompra={selectedOrdenCompra}
                onSave={async (formData) => {
                    const result = await updateOrdenCompra(selectedOrdenCompra.CLAEMP, selectedOrdenCompra.NROODC, formData);
                    if (result) {
                        alert('Orden de compra actualizada exitosamente');
                        setModalModificarOpen(false);
                        setRefresh(prev => prev + 1);
                    } else {
                        alert('Error al actualizar orden de compra: ' + updateError);
                    }
                }}
            />
            <ModalLeerOrdenCompra
                isOpen={modalLeerOpen}
                toggle={() => setModalLeerOpen(false)}
                selectedOrdenCompra={selectedOrdenCompra}
            />
        </React.Fragment>
    )
}

export default OrdenesCompra;